import React, { useEffect, useState } from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import { EMAIL_STATUS_KEYS } from "../../contract";
import { fmtDate } from "../../format";
import useClientTab from "../../hooks/useClientTab";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import CrmPager from "../../ui/CrmPager";
import CrmDialog from "../../ui/CrmDialog";
import StatusBadge, { emailStatusTone } from "../../ui/StatusBadge";
import { FOCUS_RING } from "../../ui/classes";
import { CRM } from "../../../../constants/testIds/crm";

/**
 * The read-only view of one send. The stored body is the RENDERED snapshot of what was
 * sent (C2.02 `body_html`); it is shown in a sandboxed frame with no permissions at all
 * (no scripts, no same-origin, no forms), so the email's HTML can never run in the
 * console's origin — and no HTML is injected into this document.
 */
function EmailView({ clientId, sendId, onClose }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const [send, setSend] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    crmApi.getEmail(clientId, sendId).then((d) => { if (alive) setSend(d); }).catch((e) => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [clientId, sendId]);
  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" title={send?.subject || t("emails.viewTitle")} testId={CRM.clientEmailView}>
      {error && <CrmLoadFailure error={error} />}
      {send && (
        <>
          <p className="text-sm text-eh-muted">{t("emails.meta", { from: send.from || "", to: send.to || "", date: fmtDate(send.created_at || send.sent_at, lang) })}</p>
          <iframe title={send.subject || t("emails.viewTitle")} sandbox="" srcDoc={send.body_html || ""}
            className="mt-3 h-[50vh] w-full rounded-2xl border border-eh-border-soft bg-white" />
        </>
      )}
    </CrmDialog>
  );
}

/** Emails: every send — date, subject, sequence name or "manual", status; a click opens it read-only. */
export default function EmailsTab({ clientId }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const list = useClientTab(clientId, crmApi.listEmails);
  const [open, setOpen] = useState(null);
  if (list.loadError) return <CrmLoadFailure error={list.loadError} onRetry={list.reload} />;
  if (list.loaded && list.rows.length === 0) return <EmptyState title={t("emails.empty")} />;
  return (
    <>
      <ul className="space-y-2">
        {list.rows.map((m) => {
          const tone = emailStatusTone(m.status);
          const status = EMAIL_STATUS_KEYS[m.status] ? t(EMAIL_STATUS_KEYS[m.status]) : "";
          return (
            <li key={m.id}>
              <button type="button" onClick={() => setOpen(m.id)} data-testid={CRM.clientEmail(m.id)}
                className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl bg-eh-cream px-4 py-3 text-left text-sm text-eh-ink hover:bg-eh-cream-deep ${FOCUS_RING}`}>
                <span className="text-eh-muted">{fmtDate(m.sent_at, lang)}</span>
                <span className="min-w-0 flex-1 truncate font-semibold">{m.subject}</span>
                <span className="text-eh-muted">{m.origin_label && m.origin_label !== "manual" ? m.origin_label : t("emails.manual")}</span>
                {tone ? <StatusBadge tone={tone}>{status}</StatusBadge> : <span>{status}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {list.total > list.pageSize && <CrmPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} />}
      {open && <EmailView clientId={clientId} sendId={open} onClose={() => setOpen(null)} />}
    </>
  );
}
